import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity'
import { PinpointClient, PutEventsCommand } from '@aws-sdk/client-pinpoint'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity'
import { AWS_PINPOINT_ID, AWS_MAX_EVENT, AWS_MAX_EVENT_TIME, POOL_ID, API_GATEWAY } from 'constants/appConstants'
import store from 'store/store'
import * as Sentry from '@sentry/react'
import apiClient from 'services/axiosApp'

const LIST_EVENTS = 'list_events'

const SERVER_TIME_OFFSET = 'serverTimeOffset'

export const COUNTRY_CODE_ISO3 = {
  ID: 'IDN',
  VN: 'VNM',
  PH: 'PHL',
  TH: 'THA',
}

export const generateUUID = () => {
  let e = new Date().getTime()
  let t = (performance && performance.now && 1e3 * performance.now()) || 0
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function random(n) {
    let a = 16 * Math.random()
    // eslint-disable-next-line no-return-assign
    return (
      e > 0 ? ((a = (e + a) % 16 | 0), (e = Math.floor(e / 16))) : ((a = (t + a) % 16 | 0), (t = Math.floor(t / 16))),
      // eslint-disable-next-line yoda
      ('x' === n ? a : (7 & a) | 8).toString(16)
    )
  })
}

const formatDataEndpoint = (user) => {
  const { extraInfos } = store.getState()

  // Calculate adjusted time dynamically each time
  const serverTimeOffset = Number(localStorage.getItem(SERVER_TIME_OFFSET)) || 0
  const adjustedTime = new Date(Date.now() + serverTimeOffset)

  return {
    ChannelType: 'GCM',
    Address: user.email,
    EffectiveDate: adjustedTime.toISOString(),
    User: {
      UserId: user.id,
    },
    Demographic: {
      Platform: 'Web',
      Locale: localStorage.getItem('lang') || 'en',
      Make: 'Browser',
    },
    Location: {
      Country: extraInfos.country_code,
    },
  }
}

const pinpointService = {
  client: null,

  async getServerTimeOffset() {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (!timeZone) {
        throw new Error('Time zone not found')
      }
      const { data, status } = await apiClient.get(`${API_GATEWAY}/v1/catalog/time/current?timeZone=${timeZone}`)
      if (!data || !data?.dateTime) {
        throw new Error(`HTTP error! status: ${status}`)
      }
      // Convert datetime string to timestamp in milliseconds and calculate offset
      const serverTime = new Date(data.dateTime).getTime()
      const clientTime = Date.now()
      const timeOffset = serverTime - clientTime

      localStorage.setItem(SERVER_TIME_OFFSET, timeOffset)
      return timeOffset
    } catch (error) {
      console.error('Error fetching server time, falling back to client time:', error)
      localStorage.setItem(SERVER_TIME_OFFSET, 0)
      return 0 // No offset when using fallback
    }
  },
  async setClient() {
    try {
      const timeOffset = await this.getServerTimeOffset() // Get server time offset each time when sending events
      const cognitoClient = new CognitoIdentityClient({
        region: 'ap-southeast-1',
        maxAttempts: 3,
        systemClockOffset: timeOffset,
      })
      const credentials = fromCognitoIdentityPool({
        client: cognitoClient,
        identityPoolId: POOL_ID,
        // Enable anonymous access if no authenticated user
        allowRefresh: true,
      })

      this.client = new PinpointClient({
        region: 'ap-southeast-1',
        maxAttempts: 3,
        credentials,
        systemClockOffset: timeOffset,
      })
    } catch (error) {
      Sentry.captureException('DEBUG_DLVR_PINPOINT_SET_CLIENT', {
        contexts: { error },
      })
      console.error('Error setting Pinpoint client:', error)
    }
  },
  async recordEvent(dataEvents) {
    const { currentCustomer } = store.getState()
    const userId = currentCustomer.id
    if (!this.client) await this.setClient()
    if (!this.client) return
    const params = {
      ApplicationId: AWS_PINPOINT_ID,
      EventsRequest: {
        BatchItem: {
          [userId]: {
            Endpoint: formatDataEndpoint(currentCustomer),
            Events: dataEvents,
          },
        },
      },
    }
    try {
      const command = new PutEventsCommand(params)
      const response = await this.client.send(command)
      if (response.$metadata.httpStatusCode === 202) {
        localStorage.removeItem(LIST_EVENTS)
      }
    } catch (error) {
      if (error.name === 'CredentialsProviderError' || error.name === 'UnauthorizedOperation') {
        this.client = null // Reset client for force refresh
        await this.setClient() // Create a new client with fresh credentials
        // Retry the operation
        const command = new PutEventsCommand(params)
        await this.client.send(command)
      } else {
        Sentry.captureException('DEBUG_DLVR_PINPOINT_RATE_LIMIT', {
          contexts: { error, userId, dataEvents },
        })
        console.error('Error recording event:', error)
      }
    }
  },
  formatDataEvent(eventName, attributes = {}) {
    const { currentCustomer } = store.getState()
    const companyField = currentCustomer.currentCompanyId ? { company_id: currentCustomer.currentCompanyId } : {}

    // Calculate adjusted time dynamically each time
    const serverTimeOffset = Number(localStorage.getItem(SERVER_TIME_OFFSET)) || 0
    const adjustedTime = new Date(Date.now() + serverTimeOffset)

    return {
      EventType: eventName,
      Timestamp: adjustedTime.toISOString(),
      Attributes: {
        ...attributes,
        ...companyField,
        user_id: currentCustomer.id,
      },
    }
  },
  trackEvent(eventname, attributes = {}) {
    const dataEvent = this.formatDataEvent(eventname, attributes)
    this.checkStorageDataToSendEvent(dataEvent)
  },
  checkStorageDataToSendEvent(dataEvent) {
    const listEvents = JSON.parse(localStorage.getItem(LIST_EVENTS)) || {}
    listEvents[generateUUID()] = dataEvent
    if (Object.keys(listEvents).length >= AWS_MAX_EVENT) {
      this.recordEvent(listEvents)
    } else {
      localStorage.setItem(LIST_EVENTS, JSON.stringify(listEvents))
    }
  },
  checkTimeToSendEvent() {
    setInterval(() => {
      const listEvents = JSON.parse(localStorage.getItem(LIST_EVENTS)) || {}
      if (Object.keys(listEvents).length > 0) {
        this.recordEvent(listEvents)
      }
    }, AWS_MAX_EVENT_TIME)
  },
}

export default pinpointService
